<template>
  <div class="calendar">

    <div class="buttons is-pulled-right">
      <b-button tag="router-link" to="/member-volunteer">
        Go back to Member Volunteer page
      </b-button>
    </div>
    <br clear="all" />

    <b-notification v-if="currentMonthCapped"
                    type="is-warning"
                    :closable="false">
      You have reached your maximum amount of opportunities for this Month
    </b-notification>

    <FullCalendar ref="calendar"
                  class="volunteer-calendar"
                  defaultView="dayGridMonth"
                  :plugins="calendarPlugins"
                  :header="calendarHeader"
                  :customButtons="calendarCustomButtons"
                  @dateClick="dateClick"
                  :allDaySlot="false"
                  minTime="08:00:00"
                  maxTime="19:00:00"
                  :events="fetchEvents"
                  :eventRender="renderEvent"
                  @eventClick="eventClick"
                  :datesRender="datesRender"
                  />

    <b-modal v-if="schedulingEvent"
             :active.sync="showScheduleShiftDialog"
             has-modal-card>
      <div class="modal-card">

        <section class="modal-card-body">
          <h2 class="has-text-centered">Opportunity Signup</h2>
          <br />
          <table style="border-spacing: 0.3rem; border-collapse: separate;">
            <tr>
              <td>Date:</td>
              <td>{{ schedulingEvent.extendedProps.pretty_date }}</td>
            </tr>
            <tr>
              <td>Time:</td>
              <td>{{ schedulingEvent.extendedProps.pretty_time }}</td>
            </tr>
            <tr>
              <td>Location:</td>
              <td>{{ schedulingEvent.extendedProps.site }}</td>
            </tr>
            <tr>
              <td>Activity:</td>
              <td>{{ schedulingEvent.extendedProps.activity }}</td>
            </tr>
          </table>
          <br />
          <p>Description:&nbsp; {{ schedulingEvent.extendedProps.description }}</p>
        </section>

        <footer class="modal-card-foot">
          <b-button type="is-primary"
                    @click="scheduleOpportunity()">
            Sign up
          </b-button>
        </footer>
      </div>
    </b-modal>

  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'

export default {
    name: 'Calendar',
    components: {
        FullCalendar,
    },
    data() {
        return {
            MAX_CREDITS_PER_MONTH: 2, // TODO: should this come from API?
            today: new Date(),
            inited: false,
            calendarHeader: {
                left: 'today prev,next',
                center: 'title',
                right: '',
            },
            calendarPlugins: [
                dayGridPlugin,
                timeGridPlugin,
                interactionPlugin,
            ],
            calendarCustomButtons: {
                monthButton: {
                    text: 'month',
                    click: this.monthButtonClick,
                },
            },
            showScheduleShiftDialog: false,
            schedulingEvent: {extendedProps: {}},
            myOpportunities: null,
            currentMonthCapped: false,
        }
    },
    created: function() {
        this.init()
    },
    watch: {
        '$store.state.session_established': 'init',
    },
    methods: {

        init() {
            // only need to init once
            if (this.inited) {
                return
            }

            // cannot init until session is established
            if (!this.$store.state.session_established) {
                return
            }

            // is someone logged in?
            if (this.$store.state.user.uuid) {
                // go ahead and fetch data, but only if a member-volunteer
                // *and* they are allowed to self-schedule for opportunities
                if (this.$store.state.user.member_volunteer && this.$store.state.user.volunteer_scheduling_allowed) {
                    // note, we only fetch "myOpportunities" here, b/c the calendar
                    // is responsible for fetching its own data separately
                    this.fetchMyOpportunities()
                } else { // send others back to "home" page
                    this.$router.push('/benefits')
                }
            } else { // send logged-out users to "login"
                this.$router.push('/login')
            }

            // we did it!
            this.inited = true
        },

        // render date as 'YYYY-MM-DD' string
        dateStr(date) {
            return date.toISOString().substring(0, 10)
        },

        // event source function for use with FullCalendar
        fetchEvents(info, success, failure) {

            // update "today" for sake of various other logic
            this.today = new Date()

            // note, FullCalendar assumes that `end` is *exclusive* whereas the
            // (current) veranda2 API assumes that `end_date` is *inclusive* -
            // therefore we adjust our param by one day, at least for now
            let endDate = new Date(info.end.getTime())
            endDate.setDate(endDate.getDate() - 1)

            let params = {
                start_date: this.dateStr(info.start),
                end_date: this.dateStr(endDate),
            }

            this.$http.post('/api/my/volunteering/calendar', params).then(response => {
                let events = this.convertEvents(response.data)
                success(events)
            }, response => {
                failure("could not fetch calendar data!")
            })
        },

        // convert "raw" events from API into proper FullCalendar events
        convertEvents(groupedOpportunities, xhr) {
            let events = []
            let opportunities
            groupedOpportunities.forEach(group => {

                opportunities = []
                for (let opp of group.opportunities) {
                    opportunities.push({
                        uuid: opp[0],
                        status: opp[1],
                        credits: opp[2],
                    })
                }
                
                events.push({
                    start: `${group.date} ${group.start_time}`,
                    end: `${group.date} ${group.end_time}`,
                    title: group.site,
                    extendedProps: {
                        date: group.date,
                        pretty_date: group.pretty_date,
                        weekday: group.weekday,
                        start_time: group.start_time,
                        end_time: group.end_time,
                        pretty_time: group.pretty_time,
                        site: group.site,
                        activity: group.activity,
                        description: group.description,
                        opportunities: opportunities,
                    },
                })
            })
            return events
        },

        // custom render logic for calendar events
        renderEvent(info) {
            if (info.view.type == 'timeGridDay') {

                // flag events before today as "historical"
                if (info.event.start < this.today) {
                    info.el.classList.add('historical')
                }

                // double-check the content tag; we will only modify if we
                // already have the sort of structure we expect
                let content = info.el.firstElementChild
                if (content && content.tagName == 'DIV'
                    && content.classList.contains('fc-content')) {

                    // span for "activity"
                    let activity = document.createElement('span')
                    activity.append(info.event.extendedProps.activity)
                    content.appendChild(activity)

                    // br
                    content.appendChild(document.createElement('br'))

                    // span for "available"
                    let available = document.createElement('span')
                    let opportunities = info.event.extendedProps.opportunities
                    let unassigned = opportunities.filter(opp => opp.status == 'unassigned')
                    available.append(`(${unassigned.length}/${opportunities.length}) Available`)
                    content.appendChild(available)
                }
            }
        },

        // click handler for "month" button
        monthButtonClick() {
            // show full month grid; hide right header button
            let calendarApi = this.$refs.calendar.getApi()
            calendarApi.changeView('dayGridMonth')
            this.calendarHeader.right = ''
        },

        // common logic to nagivagate calendar to given date
        showDate(date) {
            let calendarApi = this.$refs.calendar.getApi()
            calendarApi.changeView('timeGridDay', date)
            // show the month button in right of header
            this.calendarHeader.right = 'monthButton'
        },

        // click handler for "date" cell
        dateClick(info) {
            this.showDate(info.date)
        },

        // click handler for "event" item
        eventClick(info) {

            if (info.view.type == 'dayGridMonth') {
                // clicking event from month view, just shows that date view
                this.showDate(info.event.start)

            } else if (info.view.type == 'timeGridDay') {
                // just show warning if clicked event is in the past
                if (info.event.start < this.today) {
                    this.$buefy.toast.open({
                        message: "This opportunity is no longer available",
                        type: 'is-warning',
                    })
                } else {
                    // okay, current/future event here, so show popup to allow
                    // user to (try to) self-schedule for it if they want
                    this.schedulingEvent = info.event
                    this.showScheduleShiftDialog = true
                }
            }
        },

        // event hook, called after calendar renders a new date set
        datesRender(info) {
            this.updateMonthCapped(info.view.currentStart)
        },

        // update the currentMonthCapped flag based on given date
        updateMonthCapped(date) {
            if (!date) {
                let calendarApi = this.$refs.calendar.getApi()
                date = calendarApi.view.currentStart
            }
            this.currentMonthCapped = this.isMonthCapped(date)
        },

        // determine if the given date's month is "capped"
        isMonthCapped(date) {
            // note, can't determine this until we have "myOpportunities" data
            if (!this.myOpportunities) {
                return false
            }
            let monthStart = this.dateStr(new Date(date.getFullYear(), date.getMonth(), 1))
            let monthEnd = this.dateStr(new Date(date.getFullYear(), date.getMonth() + 1, 0))
            let credits = 0
            for (let opp of this.myOpportunities) {
                if (opp.date >= monthStart && opp.date <= monthEnd) {
                    credits += opp.credits
                    if (credits >= this.MAX_CREDITS_PER_MONTH) {
                        return true
                    }
                }
            }
            return false
        },

        fetchMyOpportunities() {
            this.$http.get('/api/my/volunteering/opportunities').then(response => {
                if (response.data.error) {
                    // TODO: should be smarter than just knee-jerk redirect?
                    this.$router.push('/benefits')
                } else {
                    this.myOpportunities = response.data
                    this.updateMonthCapped()
                }
            }, response => {
                // TODO: should be smarter than just knee-jerk redirect?
                this.$router.push('/benefits')
            })
        },

        scheduleOpportunity() {

            // note, we just send the first opportunity.uuid in the event.  the
            // backend is able to sort out what we "intend" by that, i.e. to
            // schedule the user for any opportunity "like" the one we specify
            let event = this.schedulingEvent
            let params = event.extendedProps.opportunities[0].uuid

            this.$http.post('/api/my/volunteering/schedule-me', params).then(response => {
                if (response.data.ok) {

                    // close dialog
                    this.showScheduleShiftDialog = false

                    // show confirmation msg
                    let msg = `You have signed up for
                        ${event.extendedProps.activity}
                        at ${event.extendedProps.site}
                        on ${event.extendedProps.weekday},
                        ${event.extendedProps.pretty_date}`
                    this.$buefy.toast.open({
                        message: msg,
                        type: 'is-success',
                        duration: 5000, // 5 seconds
                    })

                    // we want to update the "(X/Y) Available" text for the
                    // event just scheduled, but simplest thing is to let the
                    // calendar re-fetch all event data for the current date
                    let calendarApi = this.$refs.calendar.getApi()
                    let eventSource = calendarApi.getEventSources()[0]
                    eventSource.refetch()

                    // re-fetch "myOpportunities" to re-calculate month caps
                    this.fetchMyOpportunities()

                } else {
                    this.$buefy.toast.open({
                        message: response.data.error || "something went wrong!",
                        type: 'is-danger',
                    })
                }
            }, response => {
                this.$buefy.toast.open({
                    message: "something went wrong!",
                    type: 'is-danger',
                })
            })
        },

    },
}
</script>

<style lang='scss'>

@import '~@fullcalendar/core/main.css';
@import '~@fullcalendar/timegrid/main.css';
@import '~@fullcalendar/daygrid/main.css';

/* restrict calendar so it doesn't take up full screen */
.volunteer-calendar {
    margin: 0 auto;
    max-width: 1000px;
}

/* make calendar buttons use same color scheme as rest of app */
.fc-button-primary,
.fc-button-primary:disabled {
    background-color: #ab967d;
}

/* make today's date stand out a bit, in month view */
.fc-unthemed td.fc-today .fc-day-number {
    font-size: 2rem;
    font-weight: bold;
}

/* use red font for weekend day numbers in month view */
td.fc-sat .fc-day-number,
td.fc-sun .fc-day-number {
    color: darkred;
}

/* make sure user knows they can click any date in month view */
.fc-row.fc-week {
    cursor: pointer;
}

/* give "historical" events red background */
.fc-time-grid-event.historical {
    background-color: rgb(250, 227, 227);
    color: black;
}

</style>
